import React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { omit } from 'lodash';
import { Column as _ColumnBase, SortDirectionType, SortDirection } from 'react-virtualized';
import { ColumnProps } from 'react-virtualized/dist/es/Table';
import { Tooltip } from '../Tooltip';
import { Flex } from '../Flex';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { HeaderCell } from './baseTable.styles';

const ColumnBase = _ColumnBase as unknown as React.ComponentClass<ColumnProps>;

const ValueContainer = styled.div`
  width: 100% !important;
  font-size: 15px;
  font-weight: 400;
  color: #33485e;
  cursor: pointer;
`;

const TooltipHeader = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--search-text-color);
`;

const TooltipDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: var(--search-text-color);
`;

const HeaderTextLine = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--search-text-color);
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FilterPlaceholdaer = styled.div`
  width: 10px;
  margin: 0 4px;
`;

const ArrowsPlaceholdaer = styled.div`
  width: 8px;
`;

const HeaderWrapper = styled.div<{
  $centered: number;
  $iskey: number;
  $hasfilter: number;
  $direction: string | undefined;
  $isdefault: number;
}>`
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  max-width: 100%;
  padding-right: 3px;
  padding-top: 5px;
  justify-content: ${({ $centered }) => ($centered ? 'center !important' : 'flex-end !important')};
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
  fill: none;
  .columnArrows {
    fill: orange !important;
  }
  .MuiSvgIcon-root:first-of-type {
    fill: ${({ $iskey, $direction }) =>
      $iskey
        ? $direction === 'DESC'
          ? 'var(--column-arrows-svg-first-type-fill-desc)'
          : 'var(--column-arrows-svg-first-type-fill-not-desc)'
        : 'transparent'};
  }
  .MuiSvgIcon-root:last-of-type {
    fill: ${({ $iskey, $direction }) =>
      $iskey
        ? $direction === 'ASC'
          ? 'var(--column-arrows-svg-first-type-fill-asc)'
          : 'var(--column-arrows-svg-first-type-fill-not-asc)'
        : 'transparent'};
  }
  .columnFilter {
    stroke: var(--column-arrows-stroke-hover);
    ${({ $iskey, $hasfilter }) => !$hasfilter && !$iskey && 'visibility: visible;'}
  }
  :hover {
    fill: var(--column-header-hover-fill);
    .columnFilter {
      visibility: visible;
      button {
        stroke: var(--column-filter-button-stroke-hover);
      }
    }

    .MuiSvgIcon-root:first-of-type {
      fill: ${({ $iskey, $direction }) =>
        $iskey
          ? $direction === 'DESC'
            ? 'var(--column-arrows-svg-first-type-desc-hover)'
            : 'var(--column-arrows-svg-first-type-not-desc-hover)'
          : 'var(--column-arrows-svg-first-type-no-key-hover)'};
    }
    .MuiSvgIcon-root:last-of-type {
      fill: ${({ $iskey, $direction, $isdefault }) =>
        $iskey
          ? $direction === 'ASC'
            ? 'var(--column-arrows-svg-last-type-asc-hover)'
            : $isdefault
              ? 'var(--column-arrows-svg-last-type-not-asc-isdefault-hover)'
              : 'var(--column-arrows-svg-last-type-not-asc-notdefault-hover)'
          : 'var(--column-arrows-svg-last-type-no-key-hover)'};

    .columnArrows {
      stroke: var(--column-arrows-stroke-hover);
      visibility: visible;
      svg:first-of-type {
        ${({ $iskey }) => (!$iskey ? `fill: var(--column-arrows-svg-first-type-fill-hover)` : '')};
      }
      svg:last-of-type {
        ${({ $iskey }) => (!$iskey ? `fill: var(--column-arrows-svg-last-type-fill-hover)` : '')};
      }
    }
  }
`;

const RowHeader = styled(Flex)`
  align-items: center;
  text-transform: none;
  color: var(--row-header-color);
  color: #33485e;
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
  margin: 0 0 1px 0;
  justify-content: flex-end;
  width: fit-content;
  max-width: calc(100% - 16px);
  text-align: inherit;
  cursor: pointer;
`;

const ArrowsContainer = styled.div<{ $iskey: number; $direction: string | undefined; $isdefault: number }>`
  position: relative;
  padding: 0 2px 0 0 !important;
  background: var(--columns-arrows-container-background-color);
`;

const StyledArrowTopIcon = styled(ExpandLessIcon)`
  cursor: pointer;
  position: absolute;
  z-index: 1;
  top: -16px;
  height: 8px;
  stroke: none !important;
`;

const StyledArrowBottomIcon = styled(ExpandMoreIcon)`
  cursor: pointer;
  position: absolute;
  z-index: 1;
  top: -7px;
  stroke: none !important;
`;

const HeaderCellsContainer = styled('div')`
  height: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end !important;
`;

const defaultCellRenderer = ({ dataKey, cellData }: any) => {
  const isTime = dataKey === 'time' || dataKey === 'createdAt';
  const value = isTime ? dayjs(cellData).format('MM/DD/YY HH:mm:ss') : cellData;
  return <ValueContainer>{value?.toLocaleString()}</ValueContainer>;
};

const DefaultHeaderRenderer = ({
  dataKey,
  sortBy,
  sortDirection,
  disableSort,
  centered = false,
  filterOptions,
  handleSetSort,
  handleOHeaderClick,
  defaultSortBy = '',
  indexTitle,
  description,
}: any) => {
  const sortObj =
    sortBy !== dataKey
      ? { sortBy: dataKey, sortDirection: SortDirection.ASC }
      : sortDirection === SortDirection.ASC
        ? { sortBy: dataKey, sortDirection: SortDirection.DESC }
        : { sortBy: defaultSortBy, sortDirection: SortDirection.ASC };
  const dataKeyText = dataKey === 'name' && indexTitle ? indexTitle : dataKey;
  const titleArr = dataKeyText.replaceAll('_', ' ').split('\n');
  if (dataKey === 'order' || dataKey === '' || (dataKey === 'name' && !indexTitle)) return <div />;

  if (disableSort) {
    return (
      <HeaderWrapper
        $hasfilter={filterOptions ? 1 : 0}
        $iskey={sortBy === dataKey ? 1 : 0}
        $centered={centered ? 1 : 0}
        $isdefault={defaultSortBy === dataKey ? 1 : 0}
        $direction={sortDirection}
      >
        {centered && filterOptions && <FilterPlaceholdaer className="columnFilter" />}
        <RowHeader onClick={handleOHeaderClick}>
          <Tooltip
            arrow
            html={
              <>
                <TooltipHeader>{dataKeyText.replaceAll('_', ' ').replace('\n', ' ')}</TooltipHeader>
                {description && <TooltipDescription>{description}</TooltipDescription>}
              </>
            }
          >
            <HeaderCellsContainer>
              {titleArr.map((t: string, i: number) => {
                return (
                  <HeaderCell key={i}>
                    <HeaderTextLine>{t}</HeaderTextLine>
                  </HeaderCell>
                );
              })}
            </HeaderCellsContainer>
          </Tooltip>
        </RowHeader>
      </HeaderWrapper>
    );
  }

  return (
    <HeaderWrapper
      $hasfilter={filterOptions ? 1 : 0}
      $iskey={sortBy === dataKey ? 1 : 0}
      $centered={centered ? 1 : 0}
      $isdefault={defaultSortBy === dataKey ? 1 : 0}
      $direction={sortDirection}
    >
      {centered && <ArrowsPlaceholdaer className="columnArrows" />}
      {centered && filterOptions && <FilterPlaceholdaer className="columnFilter" />}
      <RowHeader onClick={() => handleSetSort && handleSetSort(sortObj)}>
        <Tooltip
          arrow
          html={
            <>
              <TooltipHeader>{dataKeyText.replaceAll('_', ' ').replace('\n', ' ')}</TooltipHeader>
              {description && <TooltipDescription>{description}</TooltipDescription>}
            </>
          }
        >
          <HeaderCellsContainer>
            {titleArr.map((t: string, i: number) => {
              return (
                <HeaderCell key={i}>
                  <HeaderTextLine>{t}</HeaderTextLine>
                </HeaderCell>
              );
            })}
          </HeaderCellsContainer>
        </Tooltip>

        <ArrowsContainer
          className="columnArrows"
          onClick={() => handleSetSort && handleSetSort(sortObj)}
          $iskey={sortBy === dataKey ? 1 : 0}
          $isdefault={defaultSortBy === dataKey ? 1 : 0}
          $direction={sortDirection}
          data-name={dataKey}
        >
          <StyledArrowBottomIcon className="arrowBottom" transform="scale(0.8, 0.8)" />
          <StyledArrowTopIcon className="arrowTop" transform="scale(0.8, 0.8)" />
        </ArrowsContainer>
      </RowHeader>
    </HeaderWrapper>
  );
};

interface ExtendedColumnProps extends ColumnProps {
  centered?: boolean;
  multiline?: boolean;
  filterOptions?: any;
  filterKey?: string;
  defaultSortBy: string;
  handleSetSort?: (sort: { sortBy: string; sortDirection: SortDirectionType | undefined }) => void;
  handleHeaderClick?: (data: any) => void;
  indexTitle?: string;
  description?: string;
  key?: string;
}

const Column: React.FC<ExtendedColumnProps> = ({
  cellRenderer = defaultCellRenderer,
  headerRenderer = DefaultHeaderRenderer,
  centered = false,
  handleSetSort,
  handleHeaderClick,
  indexTitle,
  description,
  ...props
}) => {
  const noneKeyProps = omit(props, ['key']);
  return (
    <ColumnBase
      key={props.key}
      {...{
        ...noneKeyProps,
        centered,
        cellRenderer,
        headerRenderer: (props: any) =>
          headerRenderer({ ...props, centered, handleSetSort, handleHeaderClick, indexTitle, description }),
      }}
    />
  );
};

export default Column;
